import { z } from 'zod'

/** A tenant's short handle: lowercase words joined by hyphens ("ps", "beta-bank"). */
export const tenantSlugSchema = z
  .string()
  .trim()
  .min(2, 'Use at least 2 characters')
  .max(40, 'Use at most 40 characters')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers, and single hyphens')

/** Suggest a slug from an organization name ("Beta Bank, N.A." → "beta-bank-n-a"). */
export function suggestTenantSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // drop accents NFKD split off ("é" → "e")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '')
}

/** An IANA time zone the runtime knows (e.g. "America/Denver"). */
export const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: value })
      return true
    } catch {
      return false
    }
  }, 'Unknown time zone')

export const knownTimeZoneSchema = timeZoneSchema.brand<'TimeZone'>()

/** An IANA time zone `timeZoneOf` accepted. A plain string is not one. */
export type TimeZone = z.infer<typeof knownTimeZoneSchema>

/**
 * The time zone to show for a stored value. Writes only store `timeZoneSchema`
 * output, so a value this runtime doesn't know came from outside the API; it
 * reads as UTC rather than failing the certificate or email that shows it.
 */
export function timeZoneOf(stored: string | null | undefined): TimeZone {
  const parsed = knownTimeZoneSchema.safeParse(stored)
  return parsed.success ? parsed.data : knownTimeZoneSchema.parse('UTC')
}

/** `stored` is not an IANA time zone this runtime knows. */
export class InvalidTimeZoneError extends Error {
  readonly timeZone: string

  constructor(timeZone: string) {
    super(`Unknown time zone: ${timeZone}`)
    this.name = 'InvalidTimeZoneError'
    this.timeZone = timeZone
  }
}

/**
 * The time zone for a calendar bucket (a day or a month). A wrong zone would
 * file the row under another day, so this throws instead of falling back to UTC.
 * Display paths use `timeZoneOf`.
 */
export function requireTimeZone(stored: string): TimeZone {
  const parsed = knownTimeZoneSchema.safeParse(stored)
  if (!parsed.success) {
    throw new InvalidTimeZoneError(stored)
  }
  return parsed.data
}

/**
 * A hex color as PixelSign stores and sends it: `#rrggbb`, lowercase. Branded,
 * so a plain string (a database column, say) can't pass for one without being
 * parsed: through `hexColorSchema` on write, or `accentColorOf` on read.
 */
export const normalizedHexColorSchema = z
  .string()
  .regex(/^#[0-9a-f]{6}$/, 'Use a lowercase six-digit hex color like #1d4ed8')
  .brand<'HexColor'>()

export type HexColor = z.infer<typeof normalizedHexColorSchema>

/** A six-digit hex color as a person types it, normalized to lowercase ("#2563EB" → "#2563eb"). */
export const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a six-digit hex color like #1d4ed8')
  .transform((value) => value.toLowerCase())
  .pipe(normalizedHexColorSchema)

/**
 * The accent color to show for a stored value, or null for none. Writes only
 * store `hexColorSchema` output, so a value that isn't hex came from outside
 * the API; it reads as no accent rather than failing the page that shows it.
 */
export function accentColorOf(stored: string | null): HexColor | null {
  if (stored === null) return null
  const parsed = hexColorSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1'])

/** An origin on the developer's own machine (`http://localhost:5173`). */
export function isLocalDevOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    return url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname)
  } catch {
    return false
  }
}

/**
 * An origin a signing page may be embedded in: an https origin, or a local
 * development origin (`http://localhost[:port]`, `http://127.0.0.1[:port]`).
 * Local origins are for test mode only; the API refuses them for live.
 */
export const embedOriginSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((value) => value.replace(/\/$/, ''))
  .refine((value) => {
    try {
      const url = new URL(value)
      return url.origin === value && (url.protocol === 'https:' || isLocalDevOrigin(value))
    } catch {
      return false
    }
  }, 'Use an https origin like https://app.example.com (no path); http://localhost works in test mode')

/** An https:// URL (logos are fetched by signers' browsers and mail clients). */
export const httpsUrlSchema = z
  .string()
  .trim()
  .url('Enter a full URL')
  .max(2000)
  .refine((value) => new URL(value).protocol === 'https:', 'Must be an https:// URL')
